import React, { useCallback, useMemo, useRef } from "react";
import DataGrid, { Row } from "react-data-grid";
import "react-data-grid/lib/styles.css";
import { getCellKey, isSelectableGridColumn, RDG_ROW_NUMBER_COLUMN_KEY } from "./timetableSelection";

function getEventCellPosition(event) {
    const target = event.target;
    if (!(target instanceof Element)) {
        return null;
    }

    const rowElement = target.closest(".rdg-row[data-rdg-row-idx]");
    const cellElement = target.closest(".rdg-cell");
    if (!rowElement || !cellElement) {
        return null;
    }

    const rowIdx = Number(rowElement.getAttribute("data-rdg-row-idx"));
    const colIdx = Number(cellElement.getAttribute("aria-colindex")) - 1;
    if (!Number.isInteger(rowIdx) || !Number.isInteger(colIdx)) {
        return null;
    }

    return { rowIdx, colIdx };
}

function getCellClassName(cellClass, row) {
    if (typeof cellClass === "function") {
        return cellClass(row);
    }
    return cellClass;
}

function joinClassNames(...classNames) {
    return classNames.filter(Boolean).join(" ") || undefined;
}

function isTextInputTarget(target) {
    if (!(target instanceof Element)) {
        return false;
    }

    return Boolean(target.closest("input, textarea, select, [contenteditable='true']"));
}

function isDeletedRow(row) {
    return String(row?.rawFlight?.change_status || row?.changeStatus || "").toUpperCase() === "DELETED";
}

function canEnterCellEditMode(row, column, canUpdateFlight) {
    return row?._rowType === "flight"
        && column?.key !== "flightRouteId"
        && column?.renderEditCell
        && canUpdateFlight
        && !isDeletedRow(row);
}

function TimetableRowNumberCell({ row, rowIdx }) {
    if (row?._rowType === "change-preview") {
        return <span className="schedule-draft-rdg-row-number schedule-draft-rdg-row-number-preview">原</span>;
    }

    if (row?._rowType !== "flight") {
        return null;
    }

    return <span className="schedule-draft-rdg-row-number">{row?.rowNumber ?? rowIdx + 1}</span>;
}

const TIMETABLE_HEADER_ROW_HEIGHT = 38;
const TIMETABLE_FLIGHT_ROW_HEIGHT = 38;
const TIMETABLE_CHANGE_PREVIEW_ROW_HEIGHT = 30;
const TIMETABLE_ROTATION_GAP_HEIGHT = 8;
const TIMETABLE_GRID_FRAME_HEIGHT = 2;
const CLICK_TO_EDIT_DRAG_THRESHOLD_PX = 4;
const ENTER_NAVIGATION = "next-row-same-column";
const TAB_NAVIGATION = "next-editable-cell-right";

function getTimetableRowHeight(row) {
    if (row?._rowType === "rotation-separator") {
        return 12;
    }
    if (row?._rowType === "change-preview") {
        return TIMETABLE_CHANGE_PREVIEW_ROW_HEIGHT;
    }
    return TIMETABLE_FLIGHT_ROW_HEIGHT
        + (row?._hasRotationGapAfter ? TIMETABLE_ROTATION_GAP_HEIGHT : 0);
}

function getRowIdentity(row) {
    return row?.rawFlight?.id ?? row?.id ?? null;
}

function findRowIndex(rows, row) {
    const rowIdentity = getRowIdentity(row);
    if (rowIdentity != null) {
        const identityIndex = rows.findIndex((candidate) => String(getRowIdentity(candidate)) === String(rowIdentity));
        if (identityIndex >= 0) {
            return identityIndex;
        }
    }
    return rows.indexOf(row);
}

function findColumnIndex(columns, column) {
    if (!column?.key) {
        return -1;
    }
    return columns.findIndex((candidate) => candidate?.key === column.key);
}

function findNavigationTarget({ direction, row, column, rows, columns, canUpdateFlight }) {
    const rowIdx = findRowIndex(rows, row);
    const colIdx = findColumnIndex(columns, column);
    if (rowIdx < 0 || colIdx < 0) {
        return null;
    }

    if (direction === ENTER_NAVIGATION) {
        for (let nextRowIdx = rowIdx + 1; nextRowIdx < rows.length; nextRowIdx += 1) {
            if (canEnterCellEditMode(rows[nextRowIdx], columns[colIdx], canUpdateFlight)) {
                return { rowIdx: nextRowIdx, colIdx };
            }
        }
        return null;
    }

    if (direction === TAB_NAVIGATION) {
        for (let nextColIdx = colIdx + 1; nextColIdx < columns.length; nextColIdx += 1) {
            if (canEnterCellEditMode(rows[rowIdx], columns[nextColIdx], canUpdateFlight)) {
                return { rowIdx, colIdx: nextColIdx };
            }
        }
    }

    return null;
}

const TimetableEditableGrid = ({
    columns,
    rows,
    selectedCells,
    selectedCellRange,
    selectedCellRef,
    gridRef,
    canUpdateFlight,
    onRowsChange,
    onSelectRow,
    onCellMouseDown,
    onCellMouseMove,
    onCellMouseUp,
    onColumnHeaderClick,
    onClearCellSelection,
    onCopySelectedCells,
    onColumnResize,
    onRowContextMenu,
    rowClass,
    onCellNavigation,
}) => {
    const clickToEditCandidateRef = useRef(null);
    const dataGridRef = useRef(null);
    const columnsWithSelectionClassRef = useRef([]);
    const pendingNavigationRef = useRef(null);

    const displayColumns = useMemo(
        () => [
            {
                key: RDG_ROW_NUMBER_COLUMN_KEY,
                name: "序号",
                width: 48,
                minWidth: 48,
                maxWidth: 48,
                frozen: true,
                resizable: false,
                sortable: false,
                headerCellClass: "schedule-draft-rdg-row-number-header-cell",
                renderHeaderCell: () => <span className="schedule-draft-rdg-row-number-header">序号</span>,
                renderCell: TimetableRowNumberCell,
            },
            ...columns,
        ],
        [columns],
    );

    const executeCellNavigation = useCallback(
        (navigation) => {
            if (!navigation) {
                return;
            }

            const { target, row, column, columns: activeColumns } = navigation;
            onCellNavigation?.({ position: target, row, column, columns: activeColumns });

            const selectTargetCell = () => {
                const gridHandle = dataGridRef.current;
                const position = { rowIdx: target.rowIdx, idx: target.colIdx };
                gridHandle?.scrollToCell?.(position);
                gridHandle?.selectCell?.(position, false);

                window.requestAnimationFrame(() => {
                    const gridElement = gridHandle?.element || gridRef.current?.querySelector?.(".rdg");
                    const cellElement = gridElement?.querySelector?.(`.rdg-row[data-rdg-row-idx="${target.rowIdx}"] .rdg-cell[aria-colindex="${target.colIdx + 1}"]`);
                    cellElement?.focus?.();
                });
            };

            window.requestAnimationFrame(selectTargetCell);
        },
        [gridRef, onCellNavigation],
    );

    const handleCommitNavigation = useCallback(
        ({ row, column, direction }) => {
            const activeColumns = columnsWithSelectionClassRef.current;
            const target = findNavigationTarget({ direction, row, column, rows, columns: activeColumns, canUpdateFlight });
            if (!target) {
                pendingNavigationRef.current = null;
                return;
            }

            pendingNavigationRef.current = {
                sourceRow: row,
                sourceColumn: column,
                target,
                row: rows[target.rowIdx],
                column: activeColumns[target.colIdx],
                columns: activeColumns,
            };
        },
        [canUpdateFlight, rows],
    );

    const handleDataGridRowsChange = useCallback(
        async (newRows, data) => {
            let navigation = pendingNavigationRef.current;
            pendingNavigationRef.current = null;

            if (navigation) {
                const sourceRowIdx = findRowIndex(newRows || rows, navigation.sourceRow);
                const changedRowIdx = data?.indexes?.[0];
                const changedColumnKey = data?.column?.key;
                if (sourceRowIdx !== changedRowIdx || changedColumnKey !== navigation.sourceColumn?.key) {
                    navigation = null;
                }
            }

            let result;
            try {
                result = await onRowsChange?.(newRows, data);
            } catch (error) {
                return { status: "failure", error };
            }

            if ((result === true || result?.status === "success") && navigation) {
                executeCellNavigation(navigation);
            }

            return result;
        },
        [executeCellNavigation, onRowsChange, rows],
    );

    const columnsWithSelectionClass = useMemo(
        () => displayColumns.map((column) => {
            const originalCellClass = column.cellClass;
            const originalHeaderCellClass = column.headerCellClass;
            const originalRenderHeaderCell = column.renderHeaderCell;
            const originalRenderEditCell = column.renderEditCell;
            const isRowNumberColumn = column.key === RDG_ROW_NUMBER_COLUMN_KEY;
            const isSelectableColumn = isSelectableGridColumn(column);
            const columnCellKeys = isSelectableColumn ? rows.map((row) => getCellKey(row, column)).filter(Boolean) : [];
            const isColumnSelected = columnCellKeys.length > 0 && columnCellKeys.every((cellKey) => selectedCells?.has(cellKey));
            return {
                ...column,
                headerCellClass: joinClassNames(
                    originalHeaderCellClass,
                    isSelectableColumn && "schedule-draft-rdg-header-selectable",
                    isColumnSelected && "schedule-draft-rdg-header-selected",
                ),
                renderHeaderCell: isSelectableColumn
                    ? (props) => {
                          const content = originalRenderHeaderCell ? originalRenderHeaderCell(props) : column.name;
                          return (
                              <button
                                  type="button"
                                  className="schedule-draft-rdg-header-select-button"
                                  title={`选择 ${column.name || column.key} 列`}
                                  onMouseDown={(event) => event.stopPropagation()}
                                  onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      onColumnHeaderClick?.(column, displayColumns, event);
                                  }}
                              >
                                  {content}
                              </button>
                          );
                       }
                    : originalRenderHeaderCell,
                renderEditCell: originalRenderEditCell
                    ? (props) => originalRenderEditCell({ ...props, onCommitNavigation: handleCommitNavigation })
                    : originalRenderEditCell,
                cellClass: (row) => {
                    const cellKey = getCellKey(row, column);
                    const isSelectedCell = Boolean(cellKey && selectedCells?.has(cellKey));

                    return joinClassNames(
                        getCellClassName(originalCellClass, row),
                        isRowNumberColumn && "schedule-draft-rdg-row-number-cell",
                        isSelectedCell && "schedule-draft-rdg-cell-range-selected",
                    );
                },
            };
        }),
        [displayColumns, handleCommitNavigation, onColumnHeaderClick, rows, selectedCells],
    );

    columnsWithSelectionClassRef.current = columnsWithSelectionClass;

    const handleGridMouseDown = useCallback(
        (event) => {
            if (event.button !== 0) {
                clickToEditCandidateRef.current = null;
                return;
            }

            const position = getEventCellPosition(event);
            if (!position) {
                clickToEditCandidateRef.current = null;
                const target = event.target;
                if (target instanceof Element && !target.closest(".rdg-header-row")) {
                    onClearCellSelection?.();
                }
                return;
            }

            const row = rows[position.rowIdx];
            const column = columnsWithSelectionClass[position.colIdx];
            if (column?.key === RDG_ROW_NUMBER_COLUMN_KEY) {
                clickToEditCandidateRef.current = null;
                onClearCellSelection?.();
                return;
            }

            if (row?._rowType === "flight" && isSelectableGridColumn(column)) {
                const cellKey = getCellKey(row, column);
                const isSingleSelectedCell = cellKey
                    && !event.shiftKey && !event.metaKey && !event.ctrlKey && !event.altKey
                    && selectedCells?.size === 1
                    && selectedCells.has(cellKey);
                clickToEditCandidateRef.current = isSingleSelectedCell
                    ? { cellKey, rowIdx: position.rowIdx, colIdx: position.colIdx, clientX: event.clientX, clientY: event.clientY }
                    : null;
                onCellMouseDown?.({ position, row, column, columns: columnsWithSelectionClass }, event);
            } else {
                clickToEditCandidateRef.current = null;
            }
        },
        [columnsWithSelectionClass, onCellMouseDown, onClearCellSelection, rows, selectedCells],
    );

    const clearClickToEditCandidateForMovement = useCallback((event) => {
        const candidate = clickToEditCandidateRef.current;
        if (!candidate || typeof event?.clientX !== "number" || typeof event?.clientY !== "number") {
            return;
        }
        if (Math.abs(event.clientX - candidate.clientX) > CLICK_TO_EDIT_DRAG_THRESHOLD_PX
            || Math.abs(event.clientY - candidate.clientY) > CLICK_TO_EDIT_DRAG_THRESHOLD_PX) {
            clickToEditCandidateRef.current = null;
        }
    }, []);

    const consumeClickToEditCandidate = useCallback((row, column) => {
        const candidate = clickToEditCandidateRef.current;
        clickToEditCandidateRef.current = null;
        const cellKey = getCellKey(row, column);
        return Boolean(candidate && cellKey && candidate.cellKey === cellKey);
    }, []);

    const handleGridMouseMove = useCallback(
        (event) => {
            clearClickToEditCandidateForMovement(event);
            const position = getEventCellPosition(event);
            if (position) {
                const candidate = clickToEditCandidateRef.current;
                if (candidate && (candidate.rowIdx !== position.rowIdx || candidate.colIdx !== position.colIdx)) {
                    clickToEditCandidateRef.current = null;
                }
                onCellMouseMove?.({ position, row: rows[position.rowIdx], column: columnsWithSelectionClass[position.colIdx], columns: columnsWithSelectionClass }, event);
            }
        },
        [clearClickToEditCandidateForMovement, columnsWithSelectionClass, onCellMouseMove, rows],
    );

    const handleGridContextMenu = useCallback(
        (event) => {
            const position = getEventCellPosition(event);
            if (!position) {
                return;
            }

            const row = rows[position.rowIdx];
            if (row?._rowType !== "flight") {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            onRowContextMenu?.({ position, row, column: columnsWithSelectionClass[position.colIdx], columns: columnsWithSelectionClass }, event);
        },
        [columnsWithSelectionClass, onRowContextMenu, rows],
    );

    const handleGridKeyDownCapture = useCallback(
        (event) => {
            if (event.key === "Escape" && selectedCells?.size && !isTextInputTarget(event.target)) {
                event.preventDefault();
                event.stopPropagation();
                onClearCellSelection?.();
                return;
            }

            const isCopyShortcut = (event.ctrlKey || event.metaKey) && event.key?.toLowerCase() === "c";
            if (!isCopyShortcut || !selectedCellRange || isTextInputTarget(event.target)) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            void onCopySelectedCells?.(columnsWithSelectionClass);
        },
        [columnsWithSelectionClass, onClearCellSelection, onCopySelectedCells, selectedCellRange, selectedCells],
    );

    const handleDataGridColumnResize = useCallback(
        (columnIndex, width) => {
            const column = columnsWithSelectionClass[columnIndex];
            if (!column?.key || column.key === RDG_ROW_NUMBER_COLUMN_KEY) {
                return;
            }

            onColumnResize?.(column.key, width);
        },
        [columnsWithSelectionClass, onColumnResize],
    );

    const renderRow = useCallback((key, props) => (
        <Row
            key={key}
            {...props}
            data-rdg-row-idx={props.rowIdx}
            data-rdg-row-key={props.row?.id}
        />
    ), []);
    const renderers = useMemo(() => ({ renderRow }), [renderRow]);
    const gridHeight = useMemo(
        () => (
            TIMETABLE_HEADER_ROW_HEIGHT
            + rows.reduce((totalHeight, row) => totalHeight + getTimetableRowHeight(row), 0)
            + TIMETABLE_GRID_FRAME_HEIGHT
        ),
        [rows],
    );

    return (
        <div
            ref={gridRef}
            className="schedule-draft-timetable-grid-shell"
            onMouseDown={handleGridMouseDown}
            onMouseMove={handleGridMouseMove}
            onMouseUp={onCellMouseUp}
            onContextMenu={handleGridContextMenu}
            onKeyDownCapture={handleGridKeyDownCapture}
        >
            <DataGrid
                ref={dataGridRef}
                className="schedule-draft-timetable-grid"
                style={{ blockSize: gridHeight }}
                columns={columnsWithSelectionClass}
                rows={rows}
                rowKeyGetter={(row) => row.id}
                rowHeight={getTimetableRowHeight}
                headerRowHeight={TIMETABLE_HEADER_ROW_HEIGHT}
                onRowsChange={handleDataGridRowsChange}
                onColumnResize={handleDataGridColumnResize}
                rowClass={rowClass}
                renderers={renderers}
                onCellClick={(args) => {
                    const { row, column } = args;
                    if (row?._rowType !== "flight") {
                        return;
                    }

                    const cellKey = getCellKey(row, column);
                    if (cellKey) {
                        selectedCellRef.current = cellKey;
                    }
                    onSelectRow(row);
                    if (canEnterCellEditMode(row, column, canUpdateFlight) && consumeClickToEditCandidate(row, column)) {
                        args.selectCell(true);
                    }
                }}
                onCellDoubleClick={(args) => {
                    if (canEnterCellEditMode(args.row, args.column, canUpdateFlight)) {
                        args.selectCell(true);
                    }
                }}
                defaultColumnOptions={{ resizable: true }}
            />
        </div>
    );
};

export default TimetableEditableGrid;
